// SerialTimeline — vertical event list for a serial's history.
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format/date";
import {
  PackageOpen,
  ShoppingCart,
  RotateCcw,
  ArrowRightLeft,
  Wrench,
  ShieldCheck,
  Circle,
} from "lucide-react";

interface TimelineEvent {
  type: string;
  at: string;
  label: string;
  details?: string;
}

function eventIcon(type: string) {
  if (type.startsWith("inventory_import"))   return <PackageOpen size={16} className="text-blue-500" />;
  if (type.startsWith("inventory_sale"))     return <ShoppingCart size={16} className="text-green-600" />;
  if (type.startsWith("inventory_return"))   return <RotateCcw size={16} className="text-orange-500" />;
  if (type.startsWith("inventory_transfer")) return <ArrowRightLeft size={16} className="text-purple-500" />;
  if (type.startsWith("inventory_adjust"))   return <Wrench size={16} className="text-gray-500" />;
  if (type === "warranty_claim")             return <ShieldCheck size={16} className="text-primary" />;
  return <Circle size={16} className="text-muted-foreground" />;
}

function fmt(d: string) {
  return formatDateTime(d);
}

interface Props {
  serialNo: string;
}

export function SerialTimeline({ serialNo }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["serial-timeline", serialNo],
    queryFn: () =>
      apiGet<TimelineEvent[]>(`/v1/warranty/serials/${encodeURIComponent(serialNo)}/timeline`),
    enabled: !!serialNo,
  });

  if (isLoading) return <p className="text-sm text-muted-foreground py-4">Đang tải lịch sử...</p>;
  if (isError)   return <p className="text-sm text-destructive py-4">Không thể tải lịch sử.</p>;
  if (!data || data.length === 0)
    return <p className="text-sm text-muted-foreground py-4">Chưa có lịch sử cho serial này.</p>;

  return (
    <ol className="relative border-l border-border ml-2 space-y-5 py-2">
      {data.map((ev, i) => (
        <li key={i} className="ml-5">
          {/* Icon dot */}
          <span className="absolute -left-[9px] flex h-5 w-5 items-center justify-center rounded-full bg-card border border-border">
            {eventIcon(ev.type)}
          </span>

          <div className="space-y-0.5">
            <p className="text-sm font-medium leading-tight">{ev.label}</p>
            {ev.details && (
              <p className="text-xs text-muted-foreground">{ev.details}</p>
            )}
            <p className="text-xs text-muted-foreground/70">{fmt(ev.at)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

